import React, { Fragment, ReactNode, useEffect, useState } from 'react';
import { RegisterOptions } from 'react-hook-form';
import has from 'lodash/has';
import get from 'lodash/get';
import FormInputStyleProperties from '@ui/form/FormInputStyleProperties';
import { useTranslation } from '@ui/i18n/Translation';
import Utilities from '@ui/utils/Utilities';
import _isEmpty from 'lodash/isEmpty';
import { Label, Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { ChevronUpDownIcon } from '@heroicons/react/20/solid';
import SizeTranslator, { SizeType } from '@ui/styles/SizeTranslator';

type Props = {
	isRequired?: boolean,
	placeholder?: string,
	form: any,
	name: string,
	label?: string,
	labelHint?: React.JSX.Element,
	disabled?: boolean,
	className?: string,
	extraClassName?: string,
	hideErrorMessage?: boolean,
	defaultValue?: string,
	options?: Option[]
	portal?: boolean,
	groupOptions?: GroupOptions[],
	onValueChange?: (val?: string) => any,
	optionsTextSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl',
	size?: SizeType
	optionsSize?: SizeType,
	emptyOption?: boolean
} & FormInputStyleProperties;

type Option = {
	id: string,
	label: string | ReactNode,
	value: any,
	extraInfo?: string,
	iconArea?: ReactNode
}

type GroupOptions = {
	label: string,
	options: Option[]
}

const FormSelect: React.FunctionComponent<Props> = (
	{
		form,
		placeholder,
		name,
		isRequired = false,
		label,
		isLoading,
		disabled = false,
		className,
		extraClassName,
		labelHint,
		hideErrorMessage = false,
		defaultValue,
		options,
		portal,
		groupOptions,
		optionsTextSize = 'sm',
		onValueChange,
		size = 'large',
		optionsSize = 'large',
		emptyOption = false,
	}) => {

	const { t } = useTranslation('common.form.error');
	const hasError = !!form && has(form.formState.errors, name);
	const errorMessage = hasError ? (get(form.formState.errors, name + '.message') || t('required')) : '';
	const [selectedOption, setSelectedOption] = useState<Option>();

	const validationOptions: Partial<RegisterOptions> = {
		required: isRequired,
	};

	useEffect(() => {
		form.register(name, validationOptions);
	}, []);

	const val = form.watch(name);


	useEffect(() => {
		if (!!defaultValue)
			form.setValue(name, defaultValue);
	}, [defaultValue]);

	useEffect(() => {
		if (!_isEmpty(groupOptions)) {
			setSelectedOption(findOption(val));
		}
	}, [val, groupOptions]);

	useEffect(() => {
		if (!_isEmpty(options)) {
			setSelectedOption(findOption(val));
		}
	}, [val, options]);

	const findOption = (val: any): Option | undefined => {
		let o = undefined;
		if (!!val && !_isEmpty(options)) {
			o = options!.find(o => o.value == val);
		} else if (!!val && !_isEmpty(groupOptions)) {
			groupOptions!.forEach((g: GroupOptions) => {
				const option = g.options.find(o => o.value === val);
				if (!!option) {
					o = option;
				}
			});
		}
		return o;
	};

	const onChange = (value: string) => {
		form.setValue(name, value);
		if (!!onValueChange) onValueChange(value);
	};

	const renderField = () => {
		return <>
			{!!form && <div>
				<Listbox value={val} onChange={onChange} disabled={disabled}>
					{() => <span>
							{!!label && <Label className="block text-sm font-medium leading-6 text-gray-900">
								{label}
							</Label>}
							<div className="relative mt-0.5">
								<ListboxButton
									className={Utilities.classNames(
										!!className ? className : 'input relative cursor-default pl-4 bg-white',
										Utilities.NVL(hasError, 'input-error'),
										Utilities.NVL(isLoading, 'input-isLoading'),
										Utilities.NVL(disabled, 'input-disabled'),
										!!val ? 'bg-white' : '',
										'focus:outline-none',
										'pr-4',
										SizeTranslator.translate(size),
										extraClassName)
									}>
												<span className="inline-flex w-full truncate">
														<span
															className={Utilities.classNames(!selectedOption && !!placeholder ? 'text-slate-400' : '', 'truncate')}>{!!selectedOption ? selectedOption.label : placeholder}</span>
												</span>
									<span
										className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                    <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                </span>
								</ListboxButton>

								<ListboxOptions style={{ zIndex: 1000 }}
																anchor="bottom start"
																portal={portal}
																className={`z-100 mt-1 max-h-60 overflow-auto rounded-md bg-white py-2 text-${optionsTextSize} shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none ${SizeTranslator.translate(optionsSize)}`}>
									{renderOptions()}
								</ListboxOptions>
							</div>
						</span>
					}
				</Listbox>
			</div>}
		</>;
	};

	const renderOption = (o: Option) => {
		return <ListboxOption
			key={o.id}
			className={({ focus }) =>
				Utilities.classNames(
					focus ? 'bg-gray-100 text-white' : 'text-slate-900',
					'relative cursor-default select-none py-2 pl-3 pr-2',
				)
			}
			value={o.value}
		>
			{({ selected, focus }) => (
				<>
					<div className="flex">
                    <span
											className={Utilities.classNames(selected ? 'font-bold' : 'font-normal', focus ? 'text-slate-800' : 'text-slate-500', 'truncate')}>
                      {o.label}
                    </span>
						{!!o.extraInfo && <div className="text-slate-500 ml-2 truncate">{o.extraInfo}</div>}
						{!!o.iconArea && <div className="text-slate-300 ml-2 flex items-center">{o.iconArea}</div>}
					</div>

					{selected ? (
						<span
							className={Utilities.classNames(
								focus ? 'text-white' : 'text-blue-500',
								'absolute inset-y-0 right-0 flex items-center pr-4',
							)}
						/>
					) : null}
				</>
			)}
		</ListboxOption>;
	};

	const renderOptions = () => {
		return <>
			{emptyOption && renderOption({ id: 'EMPTY_OPTION', value: null, label: placeholder ?? 'None' })}
			{!_isEmpty(options) ? options!.map(o => renderOption(o)) : ''}
			{/*@ts-ignore*/}
			{!_isEmpty(groupOptions) ? groupOptions!.map((go: any) => {
				return go.options!.map((o: Option, oidx: number) => {
					return <React.Fragment key={o.value}>
						{oidx === 0 &&
							<p
								className="block bg-gray-50 border-b border-gray-100 py-1.5 px-4 text-xs font-bold text-blue-800">&bull; {go.label}</p>}
						{renderOption(o)}
					</React.Fragment>;
				});
			}) : ''}
		</>;
	};

	return <>
		{!!labelHint ? labelHint : ''}
		{renderField()}
		{!!errorMessage && !hideErrorMessage && <p className="help help-error">{errorMessage}</p>}
	</>;

};

export default FormSelect;